import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';

const EmissionDatasetVersionFormModal = ({ show, onHide, datasetName, version, onSuccess, t, companyId = null }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [formData, setFormData] = useState({
    versionName: '',
    description: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});

  const isEdit = !!version;

  useEffect(() => {
    if (version) {
      setFormData({
        versionName: version.versionName || '',
        description: version.description || '',
        isActive: version.active !== undefined ? version.active : true
      });
    } else {
      setFormData({
        versionName: '',
        description: '',
        isActive: true
      });
    }
    setErrors({});
  }, [version, show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.versionName || formData.versionName.trim() === '') {
      newErrors.versionName = t("EmissionDatasetVersionFormModal.errors.versionNameRequired.label");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    setLoaderDisplay(true);
    const payload = {
      versionName: formData.versionName.trim(),
      description: formData.description?.trim() || null,
      isActive: formData.isActive
    };

    const promise = isEdit
      ? GHGEmissionMasterDataService.updateVersion(datasetName, version.versionNumber, payload, companyId)
      : GHGEmissionMasterDataService.createVersion(datasetName, payload, companyId);

    promise
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(
            isEdit
              ? t("EmissionDatasetVersionFormModal.updateSuccess.label")
              : t("EmissionDatasetVersionFormModal.createSuccess.label"),
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
          if (onSuccess) {
            onSuccess();
          }
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered data-testid="version-form-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit
            ? t("EmissionDatasetVersionFormModal.editTitle.label")
            : t("EmissionDatasetVersionFormModal.createTitle.label")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              {t("EmissionDatasetVersionFormModal.versionName.label")}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.versionName}
              onChange={(e) => handleChange('versionName', e.target.value)}
              isInvalid={!!errors.versionName}
              placeholder={t("EmissionDatasetVersionFormModal.versionName.placeholder.label")}
              data-testid="version-name-input"
            />
            <Form.Control.Feedback type="invalid">
              {errors.versionName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("EmissionDatasetVersionFormModal.description.label")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t("EmissionDatasetVersionFormModal.description.placeholder.label")}
              data-testid="description-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="isActive-switch"
              label={t("EmissionDatasetVersionFormModal.isActive.label")}
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              data-testid="is-active-switch"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} data-testid="cancel-button">
          {t("Cancel.label")}
        </Button>
        <Button variant="primary" onClick={handleSubmit} data-testid="submit-button">
          {isEdit ? t("Update.label") : t("Create.label")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(EmissionDatasetVersionFormModal);
